import { useState } from 'react';
import { useCondition } from '../../ExperimentsProvider';
import { Big5Questions } from './Big5Questions';
import { ValuesSelection } from './ValuesSelection';
import { InterestsSelection } from './InterestsSelection';
import { FutureSelvesSelection } from './FutureSelvesSelection';
import { AvatarFeedback, type AvatarFeedbackData } from './AvatarFeedback';
import { type AppearanceInput } from '../BaseAvatarPanel';
import { type FutureSelfStory } from './FutureSelfStories';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { useExperimentSpeculativeData } from '../../../hooks/useExperimentSpeculativeData';
import { saveSelectedAvatar } from '../utils/saveAvatar';
import { TEAME_223_AVATAR_UPDATED_EVENT } from '../useTEAME223SpeculativeAvatarData';
import { TEAME_223_FLAG_ID, TEAME_223_TREATMENT } from '../constants';

const DEFAULT_FUTURE_SELF_AVATAR_SPECULATIVE_DATA: { image?: string; has_seen?: boolean } = {};

interface PersonalityQuestionnaireProps {
  avatarImage: string;
  avatarAppearance: AppearanceInput | null;
  step: 'big5' | 'values' | 'interests' | 'futureSelves' | 'avatarFeedback';
  setStep: (step: 'big5' | 'values' | 'interests' | 'futureSelves' | 'avatarFeedback') => void;
  onComplete: (savedImageBase64?: string, avatarFeedback?: AvatarFeedbackData) => void;
}

export function PersonalityQuestionnaire({
  avatarImage,
  avatarAppearance,
  step,
  setStep,
  onComplete,
}: PersonalityQuestionnaireProps) {
  const user = useAppSelector(selectUser);
  const variantName = useCondition(TEAME_223_FLAG_ID);
  const isFutureSelvesEnabled = variantName === TEAME_223_TREATMENT;
  const userNodeId = user?.id ? `UserNode:${user.id}` : undefined;
  const {
    data: avatarSpeculativeData,
    saveSpeculativeData,
    setData: setAvatarSpeculativeData,
    isSaving: isSavingSelection,
  } = useExperimentSpeculativeData<{
    image?: string;
    has_seen?: boolean;
    avatarFeedback?: AvatarFeedbackData;
  }>({
    publicUserId: user?.sub,
    userNodeId,
    flagName: TEAME_223_FLAG_ID,
    variantName,
    defaultValues: DEFAULT_FUTURE_SELF_AVATAR_SPECULATIVE_DATA,
    enabled: isFutureSelvesEnabled && Boolean(user?.sub),
  });
  const [bigFiveAnswers, setBigFiveAnswers] = useState<Record<string, number>>({});
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedFutureSelf, setSelectedFutureSelf] = useState<FutureSelfStory | null>(null);
  const [saveError, setSaveError] = useState('');

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        const dataUrl = fileReader.result;
        if (typeof dataUrl !== 'string') {
          reject(new Error('Unable to read avatar image data'));
          return;
        }

        const base64Data = dataUrl.split(',')[1];
        if (!base64Data) {
          reject(new Error('Unable to parse avatar image data'));
          return;
        }

        resolve(base64Data);
      };
      fileReader.onerror = () => reject(new Error('Failed to convert avatar image'));
      fileReader.readAsDataURL(blob);
    });
  };

  const extractAvatarImageData = async (imageUrl: string): Promise<{ image: string }> => {
    const dataUrlMatch = imageUrl.match(/^data:(.*?);base64,(.*)$/);
    if (dataUrlMatch) {
      return {
        image: dataUrlMatch[2],
      };
    }

    const imageResponse = await fetch(imageUrl);
    if (!imageResponse.ok) {
      throw new Error('Failed to load selected avatar image');
    }

    const imageBlob = await imageResponse.blob();
    const image = await blobToBase64(imageBlob);
    return {
      image,
    };
  };

  const handleBigFiveAnswer = (questionId: string, value: number) => {
    setBigFiveAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleValueToggle = (valueAbbr: string) => {
    setSelectedValues(prev => {
      if (prev.includes(valueAbbr)) {
        return prev.filter(v => v !== valueAbbr);
      } else if (prev.length < 5) {
        return [...prev, valueAbbr];
      }
      return prev;
    });
  };

  const handleInterestToggle = (interest: string) => {
    setSelectedInterests(prev => {
      if (prev.includes(interest)) {
        return prev.filter(i => i !== interest);
      } else if (prev.length < 5) {
        return [...prev, interest];
      }
      return prev;
    });
  };

  const handleGenerateFutureSelves = () => {
    setStep('futureSelves');
  };

  const handleFutureSelfComplete = (selectedStory: FutureSelfStory) => {
    setSelectedFutureSelf(selectedStory);
    setStep('avatarFeedback');
  };

  const handleAvatarFeedbackComplete = async (feedback: AvatarFeedbackData) => {
    setSaveError('');

    if (!isFutureSelvesEnabled) {
      onComplete();
      return;
    }

    if (!userNodeId || !selectedFutureSelf?.generatedImageUrl) {
      onComplete();
      return;
    }

    try {
      const { image } = await extractAvatarImageData(selectedFutureSelf.generatedImageUrl);
      const saveResult = await saveSelectedAvatar({
        userNodeId,
        flagName: TEAME_223_FLAG_ID,
        variantName,
        image,
        avatarFeedback: feedback,
        saveToDatabase: saveSpeculativeData,
      });

      if (!saveResult.success) {
        setSaveError(saveResult.error ?? 'Unable to save selected avatar. Please try again.');
        return;
      }

      setAvatarSpeculativeData({
        ...avatarSpeculativeData,
        image,
        avatarFeedback: feedback,
        has_seen: true,
      });
      window.dispatchEvent(new Event(TEAME_223_AVATAR_UPDATED_EVENT));
      onComplete(image, feedback);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to save selected avatar. Please try again.');
    }
  };

  const imageSrc = avatarImage;

  if (step === 'futureSelves') {
    return (
      <div className="tw-flex-1 tw-min-h-0">
        <FutureSelvesSelection
          avatarImage={avatarImage}
          avatarAppearance={avatarAppearance}
          bigFiveAnswers={bigFiveAnswers}
          selectedValues={selectedValues}
          selectedInterests={selectedInterests}
          onComplete={handleFutureSelfComplete}
          completionError={saveError}
          isCompletingSelection={isSavingSelection}
        />
      </div>
    );
  }

  if (step === 'avatarFeedback' && selectedFutureSelf?.generatedImageUrl) {
    return (
      <div className="tw-flex-1 tw-min-h-0">
        <AvatarFeedback
          selectedAvatarImageUrl={selectedFutureSelf.generatedImageUrl}
          onComplete={handleAvatarFeedbackComplete}
          isSubmitting={isSavingSelection}
        />
      </div>
    );
  }

  return (
    <div className="tw-flex-1 tw-grid tw-grid-cols-[2fr_1fr] tw-min-h-0">
      <div className="tw-p-4 tw-overflow-y-auto tw-bg-white">
        {step === 'big5' && (
          <Big5Questions answers={bigFiveAnswers} onAnswer={handleBigFiveAnswer} onContinue={() => setStep('values')} />
        )}

        {step === 'values' && (
          <ValuesSelection
            selectedValues={selectedValues}
            onToggleValue={handleValueToggle}
            onContinue={() => setStep('interests')}
          />
        )}

        {step === 'interests' && (
          <InterestsSelection
            selectedInterests={selectedInterests}
            onToggleInterest={handleInterestToggle}
            onContinue={handleGenerateFutureSelves}
          />
        )}
      </div>

      <div className="tw-p-4 tw-bg-gray-50 tw-border-l tw-border-gray-200 tw-flex tw-flex-col tw-items-center tw-justify-start">
        <div className="tw-max-w-md tw-w-full tw-space-y-2">
          <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-text-center">Your Avatar</h3>
          <img
            src={imageSrc}
            alt="Your avatar"
            className="tw-w-full tw-h-auto tw-rounded-xl tw-border-4 tw-border-blue-600"
          />
        </div>
      </div>
    </div>
  );
}
